import { useWeb3React } from '@web3-react/core';
import {
    CHAIN_INFO,
    EnabledChains,
    OrderBookActionMapper,
    OrderBookType,
} from '../../config/constants';
import { formatUSD, isTpslOrder, isPendingOrder } from '../../helpers';
import AssetType from '../AssetType';
import Card from '../Card';
import TransactionBodyEntry from './TransactionBodyEntry';
import moment from 'moment';
import ExternalLink from '../ExternalLink';
import { TransactionSpinnerIcon, TxLinkIcon } from '../../assets/icons';
import clsx from 'clsx';
import { parseUnits } from 'ethers/lib/utils';
import {
    TransactionLog,
    TransactionStatusMapper,
} from '../../context/transaction/transactionTypes';
import { config } from '../../config/config';

type TransactionDetailsProps = {
    transactionDetail: TransactionLog;
    style?: React.CSSProperties;
};

const WITH_NEW_COLLATERAL = [
    OrderBookType.EXTEND_LEVERAGE,
    OrderBookType.ADD_MARGIN,
    OrderBookType.REMOVE_MARGIN,
    OrderBookType.EXTEND,
    OrderBookType.PARTIALLY_CLOSE,
];

const CLOSE_ORDERS = [
    OrderBookType.CLOSE,
    OrderBookType.PARTIALLY_CLOSE,
    OrderBookType.STOP_LOSS,
    OrderBookType.TAKE_PROFIT,
];

const TransactionDetail: React.FC<TransactionDetailsProps> = ({
    transactionDetail,
    style,
}) => {
    const { chainId } = useWeb3React();
    const {
        orderType,
        status,
        volume,
        tradePair,
        isShort,
        leverage,
        newLeverage,
        date,
        margin,
        newMargin,
        newVolume,
        estimatedPnl,
        fees,
        txHash,
        isMarketOrder,
        orderId,
    } = transactionDetail;

    const orderTypeLabel = isMarketOrder ? 'Market' : 'Limit';

    return (
        <Card className={`transaction-detail order-id-${orderId}`} style={style}>
            <div className='transaction-header'>
                <div className={clsx('order__detail', { failed: status === 'error' })}>
                    {isTpslOrder(orderType)
                        ? OrderBookActionMapper[orderType]
                        : orderType === OrderBookType.OPEN
                        ? `${orderTypeLabel} Order`
                        : `${OrderBookActionMapper[orderType]} ${orderTypeLabel}`}{' '}
                    {TransactionStatusMapper[status]}
                    {isMarketOrder && isPendingOrder(status) && (
                        <TransactionSpinnerIcon />
                    )}
                </div>
                <span className='order__size'>
                    ({formatUSD(volume, { minDecimals: 2 })})
                </span>
                <AssetType
                    tradePair={tradePair}
                    isShort={isShort}
                    leverage={leverage}
                    newLeverage={newLeverage}
                />
            </div>
            <div className='transaction-body'>
                <TransactionBodyEntry
                    title='Date'
                    value={moment.unix(date).format('Do MMM YY, HH:mm:ss')}
                />
                {orderType === OrderBookType.EXTEND && (
                    <TransactionBodyEntry
                        title='New Size'
                        value={newVolume ? formatUSD(newVolume, { minDecimals: 2 }) : '-'}
                    />
                )}
                {orderType === OrderBookType.OPEN && (
                    <TransactionBodyEntry
                        title='Collateral'
                        value={margin ? formatUSD(margin, { minDecimals: 2 }) : '-'}
                    />
                )}
                {CLOSE_ORDERS.includes(orderType) && (
                    <TransactionBodyEntry
                        title='Estimated PnL'
                        value={
                            <div
                                className={clsx('estimated-pnl', {
                                    increased: parseUnits(
                                        estimatedPnl || '0',
                                        config.priceFeedDecimal
                                    ).gt('0'),
                                    decreased: parseUnits(
                                        estimatedPnl || '0',
                                        config.priceFeedDecimal
                                    ).lt('0'),
                                })}
                            >
                                {estimatedPnl
                                    ? formatUSD(estimatedPnl, { minDecimals: 2 })
                                    : '-'}
                            </div>
                        }
                    />
                )}
                {WITH_NEW_COLLATERAL.includes(orderType) && (
                    <TransactionBodyEntry
                        title='New Collateral'
                        value={newMargin ? formatUSD(newMargin, { minDecimals: 2 }) : '-'}
                    />
                )}
                <TransactionBodyEntry
                    title='Fees'
                    value={fees ? formatUSD(fees, { minDecimals: 2 }) : '-'}
                />
                <TransactionBodyEntry
                    title='TX ID'
                    value={
                        <div
                            className={clsx('transaction-hash', {
                                failed: status === 'error',
                            })}
                        >
                            {status === 'pending' || status === 'requested' ? (
                                <i>Pending...</i>
                            ) : txHash ? (
                                <ExternalLink
                                    to={`${
                                        CHAIN_INFO[chainId as EnabledChains[number]]
                                            ?.blockExplorer
                                    }/tx/${txHash}`}
                                >
                                    <span>{txHash}</span>
                                    <TxLinkIcon />
                                </ExternalLink>
                            ) : (
                                '-'
                            )}
                        </div>
                    }
                />
            </div>
        </Card>
    );
};

export default TransactionDetail;
